import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { SignatureData, BCH_GREEN, COLOR_PRESETS, buildBchUri } from '@/lib/signature';
import {
  hasAnalyticsConsent,
  setAnalyticsConsent,
} from '@/lib/analytics';
import { useToast } from '@/hooks/useToast';
import { useState } from 'react';
import {
  User,
  Mail,
  Bitcoin,
  Share2,
  BadgeCheck,
  Palette,
  Paintbrush,
  LineChart,
  Copy,
  Check,
} from 'lucide-react';

interface SignatureFormProps {
  data: SignatureData;
  onChange: (patch: Partial<SignatureData>) => void;
}

const ACCENT_PRESETS = [
  { name: 'BCH Green', value: BCH_GREEN },
  { name: 'Emerald', value: '#059669' },
  { name: 'Ocean', value: '#0369A1' },
  { name: 'Indigo', value: '#4F46E5' },
  { name: 'Slate', value: '#334155' },
  { name: 'Amber', value: '#D97706' },
];

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function SignatureForm({ data, onChange }: SignatureFormProps) {
  const set = <K extends keyof SignatureData>(key: K, value: SignatureData[K]) =>
    onChange({ [key]: value } as Partial<SignatureData>);

  const setSocial = (key: keyof SignatureData['social'], value: string) =>
    onChange({ social: { ...data.social, [key]: value } });

  return (
    <Accordion
      type="multiple"
      defaultValue={['identity', 'contact', 'bch', 'design']}
      className="w-full"
    >
      {/* Identity */}
      <AccordionItem value="identity">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Identity
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="name" label="Full name">
              <Input
                id="name"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Satoshi Nakamoto"
              />
            </Field>
            <Field id="title" label="Job title">
              <Input
                id="title"
                value={data.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Founder & CEO"
              />
            </Field>
          </div>
          <Field id="business" label="Business name">
            <Input
              id="business"
              value={data.business}
              onChange={(e) => set('business', e.target.value)}
              placeholder="Acme Cash Co."
            />
          </Field>
          <Field id="tagline" label="Tagline / description">
            <Textarea
              id="tagline"
              value={data.tagline}
              onChange={(e) => set('tagline', e.target.value)}
              placeholder="Peer-to-peer electronic cash for everyone."
              rows={2}
            />
          </Field>
          <Field id="logo" label="Logo image URL">
            <Input
              id="logo"
              value={data.logo}
              onChange={(e) => set('logo', e.target.value)}
              placeholder="https://…/logo.png"
            />
            <p className="text-[11px] text-muted-foreground">
              Paste a direct link to your logo image (PNG, JPG or SVG).
            </p>
          </Field>
        </AccordionContent>
      </AccordionItem>

      {/* Contact */}
      <AccordionItem value="contact">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Contact
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="email" label="Email">
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
            <Field id="phone" label="Phone">
              <Input
                id="phone"
                value={data.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
              />
            </Field>
          </div>
          <Field id="website" label="Website">
            <Input
              id="website"
              value={data.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="example.com"
            />
          </Field>
          <Field id="address" label="Address">
            <Input
              id="address"
              value={data.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="21 Freedom Ave, Austin, TX"
            />
          </Field>
        </AccordionContent>
      </AccordionItem>

      {/* Bitcoin Cash */}
      <AccordionItem value="bch">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Bitcoin className="h-4 w-4 text-primary" /> Bitcoin Cash
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-1">
          <Field id="bchAddress" label="BCH payment address">
            <Input
              id="bchAddress"
              value={data.bchAddress}
              onChange={(e) => set('bchAddress', e.target.value)}
              placeholder="bitcoincash:q…"
              className="font-mono text-xs"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="bchLabel" label="Payment label (optional)">
              <Input
                id="bchLabel"
                value={data.bchLabel}
                onChange={(e) => set('bchLabel', e.target.value)}
                placeholder="Acme Cash Co."
              />
            </Field>
            <Field id="bchMessage" label="Payment note (optional)">
              <Input
                id="bchMessage"
                value={data.bchMessage}
                onChange={(e) => set('bchMessage', e.target.value)}
                placeholder="Thanks for your payment!"
              />
            </Field>
          </div>
          <p className="-mt-1 text-[11px] text-muted-foreground">
            Label &amp; note are added to the QR code (BIP-21), so they auto-fill
            in the payer&apos;s wallet.
          </p>
          <BchPayLinkButton data={data} />
          <ToggleRow
            label="Show BCH payment QR code"
            checked={data.showBchQr}
            onCheckedChange={(v) => set('showBchQr', v)}
          />
          <ToggleRow
            label={'“Accept Bitcoin Cash” badge'}
            checked={data.showAcceptBadge}
            onCheckedChange={(v) => set('showAcceptBadge', v)}
          />
          <ToggleRow
            label={'“Powered by Bitcoin Cash” badge'}
            checked={data.showPoweredBadge}
            onCheckedChange={(v) => set('showPoweredBadge', v)}
          />
        </AccordionContent>
      </AccordionItem>

      {/* Social */}
      <AccordionItem value="social">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" /> Nostr & Social
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-1">
          <Field id="nostr" label="Nostr (npub or nprofile)">
            <Input
              id="nostr"
              value={data.social.nostr ?? ''}
              onChange={(e) => setSocial('nostr', e.target.value)}
              placeholder="npub1…"
              className="font-mono text-xs"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="twitter" label="X / Twitter">
              <Input
                id="twitter"
                value={data.social.twitter ?? ''}
                onChange={(e) => setSocial('twitter', e.target.value)}
                placeholder="x.com/you"
              />
            </Field>
            <Field id="github" label="GitHub">
              <Input
                id="github"
                value={data.social.github ?? ''}
                onChange={(e) => setSocial('github', e.target.value)}
                placeholder="github.com/you"
              />
            </Field>
            <Field id="linkedin" label="LinkedIn">
              <Input
                id="linkedin"
                value={data.social.linkedin ?? ''}
                onChange={(e) => setSocial('linkedin', e.target.value)}
                placeholder="linkedin.com/in/you"
              />
            </Field>
            <Field id="telegram" label="Telegram">
              <Input
                id="telegram"
                value={data.social.telegram ?? ''}
                onChange={(e) => setSocial('telegram', e.target.value)}
                placeholder="t.me/you"
              />
            </Field>
            <Field id="instagram" label="Instagram">
              <Input
                id="instagram"
                value={data.social.instagram ?? ''}
                onChange={(e) => setSocial('instagram', e.target.value)}
                placeholder="instagram.com/you"
              />
            </Field>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Design */}
      <AccordionItem value="design">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" /> Colors &amp; theme
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-5 pt-1">
          {/* Accent */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">
              Accent color
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {ACCENT_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => set('accentColor', preset.value)}
                  title={preset.name}
                  className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    data.accentColor.toLowerCase() === preset.value.toLowerCase()
                      ? 'border-foreground scale-110'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: preset.value }}
                />
              ))}
              <label className="flex items-center gap-1.5 ml-1 cursor-pointer">
                <input
                  type="color"
                  value={data.accentColor}
                  onChange={(e) => set('accentColor', e.target.value)}
                  className="h-8 w-10 rounded-md border border-input bg-transparent p-0.5 cursor-pointer"
                  aria-label="Custom accent color"
                />
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <BadgeCheck className="h-3.5 w-3.5" /> Custom
                </span>
              </label>
            </div>
          </div>

          {/* Background presets */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">
              Background &amp; text
            </Label>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
              {COLOR_PRESETS.map((preset) => {
                const active =
                  data.bgColor.toLowerCase() === preset.bg.toLowerCase() &&
                  data.textColor.toLowerCase() === preset.text.toLowerCase();
                return (
                  <button
                    key={preset.name}
                    type="button"
                    title={preset.name}
                    onClick={() =>
                      onChange({ bgColor: preset.bg, textColor: preset.text })
                    }
                    className={`flex h-11 items-center justify-center rounded-lg border-2 text-sm font-bold transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      active ? 'border-primary' : 'border-transparent'
                    }`}
                    style={{
                      backgroundColor: preset.bg,
                      color: preset.text,
                      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                    }}
                  >
                    Aa
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom bg / text pickers */}
          <div className="grid grid-cols-2 gap-3">
            <ColorField
              icon={<Paintbrush className="h-3.5 w-3.5" />}
              label="Background"
              value={data.bgColor}
              onChange={(v) => set('bgColor', v)}
            />
            <ColorField
              icon={<BadgeCheck className="h-3.5 w-3.5" />}
              label="Text"
              value={data.textColor}
              onChange={(v) => set('textColor', v)}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Analytics */}
      <AccordionItem value="analytics" className="border-b-0">
        <AccordionTrigger className="text-sm font-semibold">
          <span className="flex items-center gap-2">
            <LineChart className="h-4 w-4 text-primary" /> Link tracking &amp; privacy
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-4 pt-1">
          <ToggleRow
            label="Track clicks on my signature links"
            checked={data.trackLinks}
            onCheckedChange={(v) => set('trackLinks', v)}
          />
          <p className="-mt-1 text-[11px] leading-relaxed text-muted-foreground">
            When on, links in your signature route through a privacy-friendly
            redirect so you can see aggregate click counts. We record only which
            <em> type</em> of link was clicked and when — never the reader, their
            email, or any personal information.
          </p>
          <ConsentToggle />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function BchPayLinkButton({ data }: { data: SignatureData }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const uri = buildBchUri(data);
  const disabled = !uri;

  const copy = async () => {
    if (!uri) return;
    try {
      await navigator.clipboard.writeText(uri);
      setCopied(true);
      toast({
        title: 'BCH pay link copied',
        description: 'Share it anywhere — it opens the payer’s wallet pre-filled.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-1.5">
      <Button
        type="button"
        variant="outline"
        onClick={copy}
        disabled={disabled}
        className="w-full gap-2"
      >
        {copied ? (
          <Check className="h-4 w-4 text-primary" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
        {copied ? 'Copied!' : 'Copy BCH pay link'}
      </Button>
      {disabled && (
        <p className="text-[11px] text-muted-foreground">
          Add a BCH payment address above to enable this.
        </p>
      )}
    </div>
  );
}

function ConsentToggle() {
  const [consent, setConsent] = useState(hasAnalyticsConsent());
  return (
    <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
      <span className="text-sm">Share anonymous usage stats</span>
      <Switch
        checked={consent}
        onCheckedChange={(v) => {
          setAnalyticsConsent(v);
          setConsent(v);
        }}
      />
    </div>
  );
}

function ColorField({
  icon,
  label,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        {icon} {label}
      </Label>
      <div className="flex items-center gap-2 rounded-lg border bg-background px-2 py-1.5">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-9 shrink-0 rounded border border-input bg-transparent p-0.5 cursor-pointer"
          aria-label={`${label} color`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 border-0 px-1 font-mono text-xs shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
